import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import MiraLogo from "../../assets/images/AppLogo/MiraLogo";
import IntlMessages from "../../util/IntlMessages";
import "./MiraFloatingButton.css";

// Mirrors the sidebar plan-access gate (hasPlanModuleAccess for `/mira`,
// whose module slug is "mira_ai").
const canAccessMira = () => {
  try {
    const raw = JSON.parse(localStorage.getItem("sidebarModuleAccess") || "[]");
    const set = new Set(Array.isArray(raw) ? raw : []);
    return set.has("mira_ai");
  } catch (error) {
    return false;
  }
};

const POSITION_STORAGE_KEY = "miraFloatBtnPosition";
const EDGE_MARGIN = 16;
const DRAG_THRESHOLD = 6;

const loadSavedPosition = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(POSITION_STORAGE_KEY));
    if (raw && (raw.side === "left" || raw.side === "right") && typeof raw.top === "number") {
      return raw;
    }
  } catch (error) {
    // ignore corrupt/missing value, fall back to default corner position
  }
  return null;
};

const savePosition = (position) => {
  try {
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify(position));
  } catch (error) {
    // ignore write failures (e.g. storage disabled)
  }
};

const MiraFloatingButton = () => {
  const history = useHistory();
  const location = useLocation();
  const companySlug = localStorage.getItem("companyDomain");

  const btnRef = useRef(null);
  const dragInfoRef = useRef(null);
  const wasDraggedRef = useRef(false);

  const [restPosition, setRestPosition] = useState(loadSavedPosition);
  const [dragPos, setDragPos] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Keep the button on-screen if the viewport shrinks after a position was saved.
  useEffect(() => {
    const clampToViewport = () => {
      const el = btnRef.current;
      if (!el) return;
      setRestPosition((prev) => {
        if (!prev) return prev;
        const maxTop = Math.max(window.innerHeight - el.offsetHeight - EDGE_MARGIN, EDGE_MARGIN);
        const top = Math.min(Math.max(prev.top, EDGE_MARGIN), maxTop);
        return top === prev.top ? prev : { ...prev, top };
      });
    };
    clampToViewport();
    window.addEventListener("resize", clampToViewport);
    return () => window.removeEventListener("resize", clampToViewport);
  }, []);

  if (!companySlug || !canAccessMira()) return null;

  const miraPath = `/${companySlug}/mira`;
  // Hide the FAB while the user is already on the Mira page.
  if (location.pathname.endsWith("/mira")) return null;

  const handlePointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    const el = btnRef.current;
    const rect = el.getBoundingClientRect();
    dragInfoRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startTop: rect.top,
      startLeft: rect.left,
      width: rect.width,
      height: rect.height,
      moved: false,
    };
    el.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event) => {
    const info = dragInfoRef.current;
    if (!info) return;
    const dx = event.clientX - info.startX;
    const dy = event.clientY - info.startY;

    if (!info.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      info.moved = true;
      setIsDragging(true);
    }

    const maxTop = Math.max(window.innerHeight - info.height - EDGE_MARGIN, EDGE_MARGIN);
    const maxLeft = Math.max(window.innerWidth - info.width - EDGE_MARGIN, EDGE_MARGIN);
    const top = Math.min(Math.max(info.startTop + dy, EDGE_MARGIN), maxTop);
    const left = Math.min(Math.max(info.startLeft + dx, EDGE_MARGIN), maxLeft);
    setDragPos({ top, left });
  };

  const endDrag = (event) => {
    const info = dragInfoRef.current;
    dragInfoRef.current = null;
    const el = btnRef.current;
    if (el && el.hasPointerCapture(event.pointerId)) {
      el.releasePointerCapture(event.pointerId);
    }
    setIsDragging(false);
    setDragPos(null);

    if (!info || !info.moved) return;

    wasDraggedRef.current = true;
    const dx = event.clientX - info.startX;
    const dy = event.clientY - info.startY;
    const maxTop = Math.max(window.innerHeight - info.height - EDGE_MARGIN, EDGE_MARGIN);
    const maxLeft = Math.max(window.innerWidth - info.width - EDGE_MARGIN, EDGE_MARGIN);
    const top = Math.min(Math.max(info.startTop + dy, EDGE_MARGIN), maxTop);
    const left = Math.min(Math.max(info.startLeft + dx, EDGE_MARGIN), maxLeft);

    // Snap to whichever end of the screen the button is closer to.
    const side = left + info.width / 2 < window.innerWidth / 2 ? "left" : "right";
    const next = { side, top };
    setRestPosition(next);
    savePosition(next);
  };

  const handleClick = (event) => {
    if (wasDraggedRef.current) {
      event.preventDefault();
      wasDraggedRef.current = false;
      return;
    }
    history.push(miraPath);
  };

  const style =
    isDragging && dragPos
      ? { top: `${dragPos.top}px`, left: `${dragPos.left}px`, right: "auto", bottom: "auto" }
      : restPosition
      ? {
          top: `${restPosition.top}px`,
          bottom: "auto",
          left: restPosition.side === "left" ? `${EDGE_MARGIN}px` : "auto",
          right: restPosition.side === "right" ? `${EDGE_MARGIN}px` : "auto",
        }
      : undefined;

  return (
    <button
      ref={btnRef}
      type="button"
      className={`mira-float-btn${isDragging ? " mira-float-btn--dragging" : ""}`}
      style={style}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      aria-label="Open Mira AI"
    >
      <MiraLogo className="mira-float-btn__icon" />
      <span className="mira-float-btn__tooltip">
        <IntlMessages id="sidebar.mira-AI" />
      </span>
    </button>
  );
};

export default MiraFloatingButton;
